import { ChangeEvent, memo, useRef } from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { Alert } from '@material-ui/lab';

import { KaapGitOpsConfigValue } from '../types';

const EMPTY_VALUE: KaapGitOpsConfigValue = {
  enabled: false,
  gitRepos: [],
  rootPath: '',
  helmRepos: [],
};

const isInvalidUrl = (url: string) =>
  url === '' || !/^https?:\/\/[^ ]+$/.test(url);

type GitOpsSectionProps = {
  value?: KaapGitOpsConfigValue;
  onChange: (value: KaapGitOpsConfigValue) => void;
};

export const GitOpsSection = memo(
  ({ value = EMPTY_VALUE, onChange }: GitOpsSectionProps) => {
    const initialEnabledRef = useRef(value.enabled ?? false);
    const initialDataRef = useRef<KaapGitOpsConfigValue>({
      enabled: value.enabled ?? false,
      gitRepos: value.gitRepos ?? [],
      rootPath: value.rootPath ?? '',
      helmRepos: value.helmRepos ?? [],
    });

    if (!initialEnabledRef.current) return null;

    const enabled = value.enabled ?? false;
    const gitRepos = value.gitRepos ?? [];
    const rootPath = value.rootPath ?? '';
    const helmRepos = value.helmRepos ?? [];

    const update = (patch: Partial<KaapGitOpsConfigValue>) =>
      onChange({ ...value, ...patch });

    const handleToggle = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        onChange(initialDataRef.current);
      } else {
        onChange({ ...value, enabled: false });
      }
    };

    const updateGitRepo = (index: number, url: string) => {
      const next = [...gitRepos];
      next[index] = url;
      update({ gitRepos: next });
    };
    const addGitRepo = () => update({ gitRepos: [...gitRepos, ''] });
    const removeGitRepo = (index: number) =>
      update({ gitRepos: gitRepos.filter((_, i) => i !== index) });

    const updateHelmRepo = (index: number, url: string) => {
      const next = [...helmRepos];
      next[index] = url;
      update({ helmRepos: next });
    };
    const addHelmRepo = () => update({ helmRepos: [...helmRepos, ''] });
    const removeHelmRepo = (index: number) =>
      update({ helmRepos: helmRepos.filter((_, i) => i !== index) });

    return (
      <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
        <Alert severity="warning">
          GitOps on KaaP tenants is deprecated. Please migrate to{' '}
          <Link
            href="/create/templates/default/daas-project"
            target="_blank"
            rel="noopener noreferrer"
          >
            DaaS
          </Link>
          , which is the supported way to manage ArgoCD and GitOps going forward.
        </Alert>
        <FormControlLabel
          control={
            <Checkbox
              checked={enabled}
              onChange={handleToggle}
              color="primary"
            />
          }
          label={
            <Typography variant="h6">(DEPRECATED) Enable GitOps</Typography>
          }
        />
        {enabled && (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <Typography>
              List all <strong>Git repositories</strong> where ArgoCD project
              resources are stored. Additionally, enter the{' '}
              <strong>Root path</strong> to the ArgoCD project in the 1st
              repository.
            </Typography>
            {gitRepos.map((url, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box
                key={`git-${i}`}
                display="flex"
                alignItems="center"
                style={{ gap: 8 }}
              >
                <TextField
                  label="Git repository"
                  value={url}
                  onChange={e => updateGitRepo(i, e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  placeholder="https://github.com/org/repo"
                  error={isInvalidUrl(url)}
                  helperText={
                    isInvalidUrl(url) ? (
                      <FormHelperText error>Must be a valid URL</FormHelperText>
                    ) : (
                      ''
                    )
                  }
                />
                {gitRepos.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeGitRepo(i)}
                    aria-label="Remove git repository"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}

            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addGitRepo}
                variant="outlined"
              >
                Add Git repository
              </Button>
            </Box>

            <TextField
              label="Root path"
              value={rootPath}
              onChange={e => update({ rootPath: e.target.value })}
              variant="outlined"
              size="small"
              fullWidth
              required
              placeholder="apps/my-project"
              helperText="Path to the ArgoCD project in the 1st repository."
            />

            <Typography>
              Optionally, enter URLs to <strong>Helm repositories</strong> to
              make available in the ArgoCD project.
            </Typography>

            {helmRepos.map((url, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box
                key={`helm-${i}`}
                display="flex"
                alignItems="center"
                style={{ gap: 8 }}
              >
                <TextField
                  label="Helm repository"
                  value={url}
                  onChange={e => updateHelmRepo(i, e.target.value)}
                  variant="outlined"
                  required
                  size="small"
                  fullWidth
                  placeholder="https://charts.example.com"
                  error={isInvalidUrl(url)}
                  helperText={
                    isInvalidUrl(url) ? (
                      <FormHelperText error>Must be a valid URL</FormHelperText>
                    ) : (
                      ''
                    )
                  }
                />
                <IconButton
                  size="small"
                  onClick={() => removeHelmRepo(i)}
                  aria-label="Remove helm repository"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addHelmRepo}
                variant="outlined"
              >
                Add Helm repository
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  },
);
